import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";
import { announceBooking } from "@/components/FirstVisitLocation";
import { toast } from "sonner";

export default function Party() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ date: "", guests: 12, child_name: "", addon_keys: [], notes: "" });
  const [payType, setPayType] = useState("deposit");
  const [avail, setAvail] = useState(null); // null=unknown, true/false
  const [busy, setBusy] = useState(false);
  const [payOrder, setPayOrder] = useState(null);

  useEffect(() => { api.get("/party/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setAvail(null);
    if (!f.date) return;
    let active = true;
    api.get(`/party/availability?date=${f.date}`).then((r) => { if (active) setAvail(r.data.available); }).catch(() => {});
    return () => { active = false; };
  }, [f.date]);

  const toggle = (k) => setF((s) => ({ ...s, addon_keys: s.addon_keys.includes(k) ? s.addon_keys.filter((x) => x !== k) : [...s.addon_keys, k] }));
  const lineFor = (a) => a.price * (a.per_child ? Math.max(f.guests || 0, 0) : 1);
  const total = cfg ? cfg.base_price + cfg.addons.filter((a) => f.addon_keys.includes(a.key)).reduce((s, a) => s + lineFor(a), 0) : 0;
  const deposit = cfg ? Math.min(cfg.deposit || 100, total) : 0;
  const amountNow = payType === "full" ? total : deposit;
  const balance = total - amountNow;
  const dueDays = cfg?.balance_due_days || 7;

  const book = async () => {
    if (!f.date) return toast.error("Pick a date for your event.");
    if (avail === false) return toast.error("That date is already reserved — please choose another day.");
    if (!f.guests || f.guests < 1) return toast.error("Enter the number of children/guests.");
    setBusy(true);
    try {
      const { data } = await api.post("/party/book", { date: f.date, guests: Number(f.guests), child_name: f.child_name, addon_keys: f.addon_keys, notes: f.notes, pay_type: payType });
      toast.success(`Party request sent! ${payType === "full" ? "Total" : "Deposit"} $${data.amount.toFixed(0)} · ref ${data.reference}`);
      setPayOrder({ payment_order_id: data.payment_order_id, amount: data.amount, reference: data.reference });
      announceBooking();
    } catch (e) { toast.error(apiErr(e)); if (/reserved/i.test(apiErr(e))) setAvail(false); }
    finally { setBusy(false); }
  };

  if (!cfg) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="max-w-xl">
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-navy flex items-center gap-2"><Icons.PartyPopper className="w-7 h-7 text-coral" /> Book a Party</h1>
      <p className="text-slate-500">Private access to the entire space for birthdays & private events.</p>

      <div className="bg-navy text-white rounded-2xl p-5 mt-6">
        <p className="text-sm text-white/60">Base booking</p>
        <p className="font-display font-black text-3xl">${cfg.base_price}</p>
        <p className="text-white/70 text-sm">{cfg.base_label}</p>
        <p className="text-turquoise text-xs mt-2">Hold your date with a ${cfg.deposit || 100} deposit — balance due {dueDays} days before your event.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Event date</Label>
            <Input type="date" value={f.date} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-lg h-10" data-testid="party-date" />
            {f.date && avail === true && <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1" data-testid="party-available"><Icons.CheckCircle2 className="w-3.5 h-3.5" /> Date available</p>}
            {f.date && avail === false && <p className="text-[11px] text-coral mt-1 flex items-center gap-1" data-testid="party-unavailable"><Icons.XCircle className="w-3.5 h-3.5" /> Date already reserved</p>}
          </div>
          <div><Label className="text-xs text-slate-500">Number of children</Label><Input type="number" min="1" value={f.guests} onChange={(e) => setF({ ...f, guests: e.target.value })} className="rounded-lg h-10" data-testid="party-guests" /></div>
        </div>
        <div><Label className="text-xs text-slate-500">Birthday child / event name (optional)</Label><Input value={f.child_name} onChange={(e) => setF({ ...f, child_name: e.target.value })} className="rounded-lg h-10" data-testid="party-child" /></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
        <p className="font-display font-bold text-navy mb-2">Add-ons</p>
        <div className="space-y-2">
          {cfg.addons.map((a) => (
            <label key={a.key} className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer ${f.addon_keys.includes(a.key) ? "border-coral bg-coral/5" : "border-slate-200"}`} data-testid={`party-addon-${a.key}`}>
              <input type="checkbox" checked={f.addon_keys.includes(a.key)} onChange={() => toggle(a.key)} />
              <div className="flex-1"><p className="font-semibold text-navy text-sm">{a.label}</p><p className="text-xs text-slate-400">{a.note}{a.per_child ? " · per child" : ""}</p></div>
              <span className="font-bold text-navy text-sm">{a.price > 0 ? `$${lineFor(a)}` : "Quote"}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
        <p className="font-display font-bold text-navy mb-2">How would you like to pay now?</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setPayType("deposit")} data-testid="party-pay-deposit" className={`rounded-xl border-2 p-3 text-left ${payType === "deposit" ? "border-turquoise bg-turquoise/5" : "border-slate-200"}`}>
            <p className="font-semibold text-navy text-sm">Pay deposit</p>
            <p className="text-xs text-slate-400">${deposit} now to hold the date</p>
          </button>
          <button type="button" onClick={() => setPayType("full")} data-testid="party-pay-full" className={`rounded-xl border-2 p-3 text-left ${payType === "full" ? "border-turquoise bg-turquoise/5" : "border-slate-200"}`}>
            <p className="font-semibold text-navy text-sm">Pay in full</p>
            <p className="text-xs text-slate-400">${total.toFixed(0)} now</p>
          </button>
        </div>
        {payType === "deposit" && balance > 0 && <p className="text-[11px] text-slate-500 mt-2">Remaining ${balance.toFixed(0)} due {dueDays} days before your event. We'll send a payment reference once your deposit is received.</p>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
        <Label className="text-xs text-slate-500">Anything else? (theme, timing, allergies)</Label>
        <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-lg h-10 mt-1" data-testid="party-notes" />
      </div>

      <div className="sticky bottom-4 mt-4 bg-white rounded-2xl border-2 border-coral p-4 flex items-center justify-between gap-3 shadow-lg">
        <div>
          <p className="text-xs text-slate-400">Estimated total</p>
          <p className="font-display font-black text-2xl text-navy" data-testid="party-estimate">${total.toFixed(0)}</p>
          <p className="text-[11px] text-slate-400">Pay now: <b data-testid="party-pay-now">${amountNow.toFixed(0)}</b>{payType === "deposit" && balance > 0 ? ` · balance $${balance.toFixed(0)}` : ""}</p>
        </div>
        <Button onClick={book} disabled={busy || avail === false} className="bg-coral text-white font-bold rounded-full h-12 px-6" data-testid="party-book-btn">{busy ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : (payType === "full" ? "Book & pay" : "Reserve with deposit")}</Button>
      </div>

      {payOrder && <PaymentDialog order={payOrder} onClose={() => setPayOrder(null)} onDone={() => { setPayOrder(null); toast.success("Thanks! We'll confirm your party shortly."); }} />}
    </div>
  );
}
